import clsx from 'clsx'
import Icon, { type IconTypes } from '@components/utils/Icon'

// --- SimpleStatCard ---

type SimpleStatCardProps = {
  value: number
  label: string
}

export function SimpleStatCard({ value, label }: SimpleStatCardProps) {
  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-4">
      <span className="tw:text-2xl tw:font-bold tw:text-gray-900">{value}</span>
      <span className="tw:text-sm tw:font-medium tw:text-gray-500">{label}</span>
    </div>
  )
}

// --- SplitStatCard ---

type SplitStatCardProps = {
  value: number
  label: string
  size?: 'default' | 'sm'
  children: React.ReactNode
}

const shellStyles = {
  default: {
    container: 'tw:bg-white tw:p-4',
    value: 'tw:text-2xl',
    label: 'tw:text-sm',
    leftGap: 'tw:gap-1',
    leftPadding: 'tw:pb-3 tw:sm:pb-0 tw:sm:pr-4',
    rightPadding: 'tw:pt-3 tw:sm:pt-0 tw:sm:pl-4',
    leftMinWidth: '',
  },
  sm: {
    container: 'tw:bg-gray-50 tw:p-3',
    value: 'tw:text-lg',
    label: 'tw:text-xs',
    leftGap: 'tw:gap-0.5',
    leftPadding: 'tw:pb-2 tw:sm:pb-0 tw:sm:pr-3',
    rightPadding: 'tw:pt-2 tw:sm:pt-0 tw:sm:pl-3',
    leftMinWidth: 'tw:sm:min-w-[60px]',
  },
}

export function SplitStatCard({ value, label, size = 'default', children }: SplitStatCardProps) {
  const s = shellStyles[size]

  return (
    <div
      className={clsx(
        'tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:rounded-lg tw:border tw:border-gray-200',
        s.container
      )}
    >
      <div
        className={clsx(
          'tw:flex tw:w-full tw:sm:w-auto tw:flex-col tw:items-center tw:border-b tw:sm:border-b-0 tw:sm:border-r tw:border-gray-200',
          s.leftGap,
          s.leftPadding,
          s.leftMinWidth
        )}
      >
        <span className={clsx('tw:font-bold tw:text-gray-900', s.value)}>{value}</span>
        <span className={clsx('tw:font-medium tw:text-gray-500', s.label)}>{label}</span>
      </div>
      <div className={clsx('tw:flex tw:flex-col tw:gap-1', s.rightPadding)}>
        {children}
      </div>
    </div>
  )
}

// --- BreakdownLine ---

type BreakdownLineProps = {
  icon: IconTypes
  iconColor: string
  label: string
  size?: 'default' | 'sm'
}

const breakdownStyles = {
  default: { text: 'tw:text-sm', icon: 'tw:h-4 tw:w-4' },
  sm: { text: 'tw:text-xs', icon: 'tw:h-3.5 tw:w-3.5' },
}

export function BreakdownLine({ icon, iconColor, label, size = 'default' }: BreakdownLineProps) {
  const s = breakdownStyles[size]

  return (
    <div className={clsx('tw:flex tw:items-center tw:gap-1.5 tw:text-gray-700', s.text)}>
      <Icon type={icon} className={clsx('tw:shrink-0', s.icon, iconColor)} />
      <span>{label}</span>
    </div>
  )
}
